import { VaultedSecretAccess } from "@p0security/integrations-vaulted-credential-shared/accesses";
import { Access } from "@p0security/shared/types/access/index";

import { JiraAiLevels } from "./tools.ts";

/** Where a secret is, as the connector's contract names it. */
const secretRef = {
  properties: {
    manager: { choices: ["gcp"], type: "string" },
    projectId: { type: "string" },
    secretId: { type: "string" },
  },
  type: "object",
} as const;

/** The agentic catalog key of the Jira (agent pool) MCP server, the one
 * server a jira-ai grant writes entitlements on. It is stored as each added
 * server's `definition.id`, so it never changes. */
export const JIRA_AGENT_POOL_SERVER = "jiraAgentPool";

/** The role an approver sees for a request with no project: it gets no
 * project role. */
export const SITE_WIDE_ROLE = "None: site-wide tools only";

export const JiraAiAccesses = {
  tool: Access({
    delegation: { gcpSm: VaultedSecretAccess.delegation.gcpSm },
    description:
      "Lets an agent session call Jira tools in one project, or site-wide read tools, through one of the site's Jira agent accounts",
    generated: {
      ...VaultedSecretAccess.generated,
      until: { label: "Registered expiration time", type: "string" },
      // What a revoke needs, recorded at stage so that it never reads the
      // install items, which may be gone by then.
      accountId: { label: "Jira agent account ID", type: "string" },
      poolAccount: { label: "Jira agent account", type: "string" },
      poolSecret: { ...secretRef, label: "Agent account API key" },
      site: {
        label: "Jira site",
        properties: {
          cloudId: { type: "string" },
          adminClientId: { type: "string" },
          adminSecret: secretRef,
          // The P0 tenant the grant is for. The connector refuses a site
          // whose P0 roles another tenant manages.
          tenantId: { type: "string" },
        },
        type: "object",
      },
      hosting: {
        label: "Connector hosting",
        properties: {
          type: { choices: ["gcp"], type: "string" },
          projectId: { type: "string" },
          connectorName: { type: "string" },
          connectorRegion: { type: "string" },
          connectorServiceAccount: { type: "string" },
          connectorServiceUri: { type: "string" },
        },
        type: "object",
      },
      // None for a site-wide request, which gets no project role.
      projectKey: { label: "Jira project", optional: true, type: "string" },
      // The connector's role policy: one of P0's roles by its level, in a
      // company-managed project, or a team-managed project's role by its id.
      // Flat, since a generated field can't be a union. The backend parses it
      // back with the connector's schema. None for a site-wide request.
      policy: {
        label: "Jira role",
        optional: true,
        properties: {
          level: { choices: JiraAiLevels, optional: true, type: "string" },
          roleId: { optional: true, type: "string" },
          type: { choices: ["p0", "space"], type: "string" },
        },
        type: "object",
      },
      // The requested role's id in a team-managed project, and none in a
      // company-managed one or a site-wide request. A revoke takes the
      // account out of these as well as out of P0's own roles.
      roleIds: { label: "Jira role IDs", multivalued: true, type: "string" },
    },
    label: "Jira tools",
    maturity: "beta",
    parameters: {
      gateway: {
        description: "Gateway that hosts agentic access",
        label: "Gateway",
        type: "input",
      },
      server: {
        description: "Gateway-hosted MCP server",
        label: "MCP server",
        type: "input",
      },
      site: {
        description: "The Jira site, as installed in P0",
        label: "Jira site",
        type: "dynamic",
      },
      // Optional, so the CLI takes it as --project: a request for site-wide
      // tools only names none.
      project: {
        description:
          "The key of a Jira project, such as ENG. Leave it out only when every tool is site-wide, such as listJiraProjects",
        label: "Project",
        optional: true,
        type: "input",
      },
      tool: {
        description: "The Jira tools the agent may call",
        label: "Tool(s)",
        multivalued: true,
        type: "static",
      },
      // Optional, so the CLI takes it as --role.
      role: {
        description:
          "For a team-managed project only: the name of the project role to grant, such as Member",
        label: "Project role",
        optional: true,
        type: "input",
      },
      // Generated, so the approver sees the role the grant gives: one of
      // P0's roles in a company-managed project, or the requested role in a
      // team-managed one, or SITE_WIDE_ROLE for a request with no project.
      roles: { label: "Jira role", multivalued: true, type: "generated" },
      // Names the request's own token secret. Chosen at random when the
      // request is created, because `delegation` sees no request id.
      tokenId: { label: "Token ID", type: "generated" },
    },
    render: { locator: "site", policy: "tool", resource: "project" } as const,
  }),
};

export type JiraAiAccesses = typeof JiraAiAccesses;
export type JiraAiToolAccess = JiraAiAccesses["tool"];
